/**
 * Agent mandates, read from the work's side, the agent's and the gallery's.
 */
import type {
  AgentMandatesResponse,
  AssignAgentRequest,
  GalleryAgentRosterResponse,
  RenewMandateRequest,
  SetMandateScopeRequest,
  WorkAgentsResponse,
  WorkRepresentationResponse,
} from "@ondo/shared";

import { request, requestWith } from "./request.ts";

/** Who represents a work. The owner and each agent see it; nobody else. */
export const fetchWorkAgents = (workId: string) =>
  request<WorkAgentsResponse>(`/works/${workId}/agents`).then((body) => body.agents);

/**
 * Who represents a work, as told to everybody the work reaches — OND-73. The
 * viewer's question, not the owner's: no terms, no note, and a `standing` that
 * SQL computed so an expired or revoked appointment can never read as current.
 */
export const fetchWorkRepresentation = (workId: string) =>
  request<WorkRepresentationResponse>(`/works/${workId}/representation`);

export const assignWorkAgent = (workId: string, body: AssignAgentRequest) =>
  request<void>(`/works/${workId}/agents`, body);

/**
 * Extends a mandate's term.
 *
 * POST AND NOT PATCH, and the mandate is append-only so this is not a general
 * edit: the ONLY thing it can move is the date, forward, and
 * `ondo.renew_work_agent_mandate()` refuses everything else — including a
 * shorter term, a revoked mandate and an open-ended one. A PATCH would suggest
 * the row had editable fields, which is the reading the append-only trigger
 * exists to prevent.
 *
 * EITHER SIDE ALONE MAY CALL IT. There is no proposal to accept and nothing to
 * poll: the other party is told by the warnings they stop receiving, and the
 * renewal row records which authority was used.
 */
export const renewAgentMandate = (
  workId: string,
  mandateId: string,
  body: RenewMandateRequest,
) => request<void>(`/works/${workId}/agents/${mandateId}/renew`, body);

/**
 * Replaces an appointment's recorded scope: who it reaches, what it permits.
 *
 * POST AND NOT PATCH, for `renewAgentMandate`'s reason — the mandate is
 * append-only and this is not a general edit. What it CAN move is exactly two
 * fields, and `ondo.set_work_agent_mandate_scope()` refuses everything else,
 * including a revoked appointment and a change that changes nothing.
 *
 * THE BODY IS THE WHOLE SCOPE, so a null `organisationOndoId` REMOVES the
 * organisation rather than leaving it alone — see `SetMandateScopeRequest`.
 * `toScopeRequest` is what turns the row's draft into it, and it seeds that
 * draft from the appointment precisely so an unedited save is a no-op rather
 * than a silent narrowing.
 *
 * THE APPOINTING SIDE ONLY, which the screen knows as `canSetScope` and the
 * database knows as three branches with no fourth. An agent calling it on their
 * own appointment is refused.
 */
export const setAgentMandateScope = (
  workId: string,
  mandateId: string,
  body: SetMandateScopeRequest,
) => request<void>(`/works/${workId}/agents/${mandateId}/scope`, body);

/**
 * Ends a mandate. DELETE, though the row is not deleted — `ended_at` is set and
 * anything the agent wrote while it was live stays written.
 */
export const endAgentMandate = (workId: string, mandateId: string) =>
  requestWith<void>("DELETE", `/works/${workId}/agents/${mandateId}`);

/** The agent's own side: the works this account represents. */
export const fetchAgentMandates = () =>
  request<AgentMandatesResponse>("/agent-mandates/mine").then((body) => body.mandates);

/**
 * The gallery's own side: every work the FIRM has placed with an agent.
 *
 * Takes no organisation id, because the server resolves the firm from the
 * caller's memberships — there is nothing here for a client to supply and so
 * nothing for it to get wrong. A gallery with nothing out reads an empty list
 * rather than an error.
 */
export const fetchGalleryAgentRoster = () =>
  request<GalleryAgentRosterResponse>("/agents/roster").then((body) => body.placements);
